/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useEmailConfigForm.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing email config form state and validation.
────────────────────────────────────────────────────────────────*/

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect, useRef } from 'react';
import type { EmailConfig, EmailAuthType } from '../types';

export const emailConfigSchema = z.object({
  name: z.string().min(1, 'Configuration name is required').trim(),
  host: z.string().min(1, 'Host is required').trim(),
  port: z.number().min(1).max(65535, 'Port must be between 1 and 65535'),
  secure: z.boolean(),
  tls: z.boolean(),
  authType: z.enum(['none', 'plain', 'login', 'oauth2']),
  username: z.string().optional(),
  password: z.string().optional(),
  fromEmail: z.string().min(1, 'From email is required').email('Invalid email format').trim(),
  fromName: z.string().min(1, 'From name is required').trim(),
  replyTo: z.string().email('Invalid email format').optional().or(z.literal('')),
  connectionTimeout: z.number().optional(),
  greetingTimeout: z.number().optional(),
  socketTimeout: z.number().optional(),
  isActive: z.boolean(),
  isDefault: z.boolean(),
});

export type EmailConfigFormData = z.infer<typeof emailConfigSchema>;

export const PLACEHOLDER_PASSWORD = '[ENCRYPTED]';

export const DEFAULT_FORM_VALUES: EmailConfigFormData = {
  name: '',
  host: '',
  port: 587,
  secure: false,
  tls: true,
  authType: 'none',
  username: '',
  password: '',
  fromEmail: '',
  fromName: '',
  replyTo: '',
  connectionTimeout: 60000,
  greetingTimeout: 30000,
  socketTimeout: 60000,
  isActive: true,
  isDefault: false,
};

export const AUTH_TYPES: Array<{ value: EmailAuthType; labelKey: string }> = [
  { value: 'none', labelKey: 'emailSettings.authTypes.none' },
  { value: 'plain', labelKey: 'emailSettings.authTypes.plain' },
  { value: 'login', labelKey: 'emailSettings.authTypes.login' },
  { value: 'oauth2', labelKey: 'emailSettings.authTypes.oauth2' },
];

export const AUTH_CREDENTIALS_REQUIRED: EmailAuthType[] = ['plain', 'login', 'oauth2'];

export function mapConfigToFormData(
  config: EmailConfig,
  duplicate = false
): EmailConfigFormData {
  return {
    name: duplicate && config.name ? `${config.name}_copy` : config.name ?? '',
    host: config.host ?? '',
    port: Number(config.port ?? DEFAULT_FORM_VALUES.port),
    secure: Boolean(config.secure),
    tls: Boolean(config.tls ?? true),
    authType: (config.authType ?? 'none') as EmailAuthType,
    username: config.username ?? '',
    password: duplicate ? '' : config.password ? PLACEHOLDER_PASSWORD : '',
    fromEmail: config.fromEmail ?? '',
    fromName: config.fromName ?? '',
    replyTo: config.replyTo ?? '',
    connectionTimeout: config.connectionTimeout ?? DEFAULT_FORM_VALUES.connectionTimeout,
    greetingTimeout: config.greetingTimeout ?? DEFAULT_FORM_VALUES.greetingTimeout,
    socketTimeout: config.socketTimeout ?? DEFAULT_FORM_VALUES.socketTimeout,
    isActive: config.isActive ?? true,
    isDefault: duplicate ? false : config.isDefault ?? false,
  };
}

export function useEmailConfigForm(initialConfig: EmailConfig | null, isDuplicate: boolean) {
  const form = useForm<EmailConfigFormData>({
    resolver: zodResolver(emailConfigSchema),
    mode: 'onSubmit',
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const { reset, watch, setValue, getValues } = form;
  const autoPortInitializedRef = useRef(false);
  const secure = watch('secure');
  const tls = watch('tls');

  // Initialize form with config data
  useEffect(() => {
    if (initialConfig) {
      const formData = mapConfigToFormData(initialConfig, isDuplicate);
      reset(formData);
      autoPortInitializedRef.current = true;
    } else if (!initialConfig && !isDuplicate) {
      reset(DEFAULT_FORM_VALUES);
      autoPortInitializedRef.current = false;
    }
  }, [initialConfig, isDuplicate, reset]);

  // Auto-set port based on security settings
  useEffect(() => {
    if (!autoPortInitializedRef.current) {
      autoPortInitializedRef.current = true;
      return;
    }

    const currentPort = getValues('port');

    if (secure) {
      if (currentPort !== 465) {
        setValue('port', 465, { shouldDirty: true });
      }
      if (tls) {
        setValue('tls', false, { shouldDirty: true });
      }
      return;
    }

    if (tls) {
      if (currentPort !== 587) {
        setValue('port', 587, { shouldDirty: true });
      }
      return;
    }

    if ([465, 587].includes(currentPort)) {
      setValue('port', 25, { shouldDirty: true });
    }
  }, [secure, tls, getValues, setValue]);

  return {
    ...form,
    setError: form.setError,
  };
}

